<template>
	<div class="space-y-xs">
		<v-button
			v-if="showLiveConfigurator && !liveConfiguratorLoading"
			role="button"
			theme="gray"
			class="mb-2 font-sans! tracking-3xl! normal-case!"
			@click="openLiveConfigurator()"
		>
			{{ t("open-picker") }}
			<span class="">🎨</span>
		</v-button>

		<v-page-product-configurator-attribute
			v-for="(configurableAttribute, index) in configuration.attributes"
			:key="configurableAttribute.attribute.id"
			v-model="selections"
			:index="index"
			:configurable-attribute="configurableAttribute"
			@update:model-value="clearSelections(index)"
		/>

		<v-page-product-configurator-property
			v-for="property in productProperties"
			:key="property.id"
			v-model="propertyValues[property.id]"
			:product-property="property"
		/>
	</div>
</template>

<script setup lang="ts">
import {useVModel, whenever} from "@vueuse/core";
import {intersection} from "es-toolkit";
import {
	type ConfigurableProductDataFragment,
	type ViewProductQuery,
	ProductMaskMediaType,
} from "@pkg/shared/graphql";
import type {ProductKind} from "@pkg/shared/types";

type ConfigurableProduct = ConfigurableProductDataFragment;
type Configuration = ConfigurableProduct["configuration"];
type ProductProperty = ProductKind<
	ViewProductQuery["product"]
>["properties"][0];
type ConfigurableAttributeOption = Configuration["attributes"][0]["options"][0];
type SelectedProduct = Configuration["products"][0];

const properties = defineProps<{
	selectedProduct?: SelectedProduct;
	selectedProperties: Record<string, string>;
	configurableProduct?: ConfigurableProduct;
	configuration: Configuration;
	productProperties: ProductProperty[];
	showLiveConfigurator: boolean;
}>();

const emit = defineEmits<{
	(event: "update:selectedProduct", value: SelectedProduct | undefined): void;
	(event: "update:selectedProperties", value: Record<string, string>): void;
}>();

const {t} = useI18n();
const nuxtImg = useImage();

const selections = ref<(ConfigurableAttributeOption | undefined)[]>([]);
const propertyValues = useVModel(properties, "selectedProperties", emit);

whenever(
	() => properties.selectedProduct,
	(value) => {
		selections.value = properties.configuration.attributes.map(
			(configurableAttribute) =>
				configurableAttribute.options.find((option) =>
					option.products.some((item) => item.id === value?.id),
				),
		);
	},
	{immediate: true},
);

const productIds = computed(() => {
	const selectedProductIds = selections.value.map(
		(option) => option?.products.map((product) => product.id) || [],
	);

	let result: string[] = [];

	for (const [index, ids] of selectedProductIds.entries()) {
		result = index === 0 ? ids : intersection(result, ids);
	}

	return result;
});

watch(productIds, (value) => {
	if (value.length === 1) {
		const product = properties.configuration.products.find(
			(item) => item.id === value[0],
		);

		emit("update:selectedProduct", product);
	} else {
		emit("update:selectedProduct", undefined);
	}
});

function clearSelections(index: number) {
	selections.value = selections.value.slice(0, index + 1);
}

const liveConfiguratorLoading = ref(true);

function getLiveConfiguratorProps() {
	const {configurableProduct} = properties;
	const maskMedias = configurableProduct?.maskMedias ?? [];

	// Currently we only support one batch of medias, and its name needs to be "main"
	const baseMedia = maskMedias.find(
		(x) => x.specifier === "main" && x.type === ProductMaskMediaType.Base,
	);
	const maskMedia = maskMedias.find(
		(x) => x.specifier === "main" && x.type === ProductMaskMediaType.ColorMask,
	);

	const colorAttrIndex = properties.configuration.attributes.findIndex(
		(x) => x.attribute.code === "color",
	);
	const colorOptions =
		properties.configuration.attributes[colorAttrIndex]?.options;

	if (!(baseMedia && maskMedia && colorOptions)) {
		return undefined;
	}

	const otherAttributes = [...properties.configuration.attributes];
	otherAttributes.splice(colorAttrIndex, 1);

	// Fabric color selection is its own thing in the live configurator
	// These are boilerplate-y, just the format the live configurator expects
	const fabricsConfig = [
		{
			ryhma: "Kangas",
			fabrics: [
				{
					type: "Väri",
					images: [
						{
							name: "main",
							baseUrl: baseMedia.url,
							maskUrl: maskMedia.url,
						},
					],
					variants: colorOptions
						.map((x) => ({
							name: x.option.title,
							color: x.option.color,
							pilling: "5",
							marindale: "9000",
							kuvaus: "",
							optionId: x.option.id,
							attributeIndex: colorAttrIndex,
						}))
						.filter((x) => !!x.color),
				},
			],
		},
	];

	// Other attributes go here
	const attrsConfig = otherAttributes.map((attr, attrIdx) => ({
		type: attr.attribute.id,
		name: attr.attribute.title,
		variants: attr.options.map((opt) => {
			// This is the concrete original index of the parent attribute when we take into account that the color attribute got shifted to be first.
			const realAttrIdx = attrIdx >= colorAttrIndex ? attrIdx + 1 : attrIdx;

			// This is a dynamic value since it depends on the selections the user has made so far. The live configurator will call this when a new option gets selected, but before our `selections` value updates to reflect that.
			const getOverlayUrl = () => {
				for (const media of maskMedias) {
					const otherLinkedOptions = media.linkedOptions.filter(
						(x) => x.id !== opt.option.id,
					);
					// Have other options linked to the media already been selected? Note that the current option won't be here since it's been
					const otherLinkedOptionsSelected = otherLinkedOptions.every(
						(linkedOpt) =>
							selections.value.some(
								(selectedOpt) => selectedOpt?.option.id === linkedOpt.id,
							),
					);
					// Is this option part of the current media's linked options?
					const isLinkedToOption = media.linkedOptions.some(
						(x) => x.id === opt.option.id,
					);
					// Return the media if its other linked options have been selected already, and this current option is the last one missing
					if (
						isLinkedToOption &&
						otherLinkedOptionsSelected &&
						media.linkedOptions.length > 0
					) {
						return media.url;
					}
				}
				return null;
			};

			// Same logic as `page-product-configurator-attribute`. First find all applicable variants for earlier selections that the user has made (by intersecting applicable products from each step), then finally intersect variants from this current option that's being examined. If any variants are applicable, this option is selectable.
			// The live configurator will call this only after our `selections` has updated
			const getEnabled = () => {
				const earlierMatchingProductIds = selections.value
					.slice(0, realAttrIdx)
					.map((option) => option?.products.map((product) => product.id) || [])
					.reduce((acc, ids) => intersection(acc, ids));
				const ownProductIds = opt.products.map((x) => x.id);
				return (
					intersection(ownProductIds, earlierMatchingProductIds).length > 0
				);
			};

			// If no medias point to any of this attribute's options, consider the options non-visual
			const isNonVisual = attr.options.every((attrOpt) =>
				maskMedias.every((media) =>
					media.linkedOptions.every(
						(linkedOpt) => linkedOpt.id !== attrOpt.option.id,
					),
				),
			);

			return {
				name: opt.option.title,
				overlayUrls: {
					main: getOverlayUrl,
				},
				optionId: opt.option.id,
				attributeIndex: realAttrIdx,
				enabled: getEnabled,
				isNonVisual,
			};
		}),
	}));

	const urlToCdn = (url: string) =>
		nuxtImg(url, {
			width: 720,
			height: 720,
			format: "webp",
		} as Parameters<typeof nuxtImg>[1] & {format: "webp"});

	return {
		fabricsConfig,
		attrsConfig,
		urlToCdn,
	};
}

function openLiveConfigurator() {
	if (import.meta.server) {
		return;
	}
	if (!(
		"openProductConfigurator" in window &&
		typeof window.openProductConfigurator === "function"
	)) {
		return;
	}
	const props = getLiveConfiguratorProps();
	if (!props) {
		return;
	}
	window.openProductConfigurator(props);
}

if (import.meta.client) {
	// React to selections in the live configurator by selecting the appropriate option

	document.addEventListener(
		"live-product-configurator-option-select",
		(e: Event) => {
			const {optionId, attributeIndex} = (
				e as CustomEvent<{optionId: string; attributeIndex: number}>
			).detail;

			const attribute = properties.configuration.attributes[attributeIndex];

			if (!attribute) {
				return;
			}

			const option = attribute.options.find((x) => x.option.id === optionId);
			selections.value[attributeIndex] = option;
			clearSelections(attributeIndex);
		},
	);
}

const {onLoaded: onLiveConfiguratorLoad} = useScript({
	src: "/scripts/product-configurator.js",
	async: true,
});

onLiveConfiguratorLoad(() => {
	const props = getLiveConfiguratorProps();
	// If this isn't an applicable product (some data can't be resolved), don't show the button
	if (props) {
		liveConfiguratorLoading.value = false;
	}
});
</script>

<i18n lang="json">
{
	"et": {
		"open-picker": "Ava live-valija!"
	},
	"fi": {
		"open-picker": "Avaa live-valitsin!"
	}
}
</i18n>
